/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

// reference to maps removed
import * as Msdyn365 from '@msdyn365-commerce/core';
import MsDyn365 from '@msdyn365-commerce/core';
import { reaction } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
// VSI Customization - starts
import { StoreSelectorStateInput } from '../../data-actions/store-selector-state-data-action';
// VSI Customization - ends
import {
    StoreSelectorEmptyMessage,
    StoreSelectorFoundLocationsMessage,
    StoreSelectorLocationLines,
    StoreSelectorSearchForm,
    // VSI Customization - starts
    StoreSelectorSeeAllStores,
    // VSI Customization - ends
    StoreSelectorTermsOfService,
    StoreSelectorWaiting
} from './components';
import { SociLocationLines } from './components/soci-locations-lines';
import { IMfrmStoreSelectorData } from './mfrm-store-selector.data';
import { IMfrmStoreSelectorProps, mode as modeEnum, searchRadiusUnit, style as stylyEnum } from './mfrm-store-selector.props.autogenerated';

import { IStoreInfo } from '@msdyn365-commerce-modules/bopis-utilities';
import {
    getFullAvailableInventoryNearby,
    GetFullAvailableInventoryNearbyInput,
    getOrgUnitLocationsByArea,
    GetOrgUnitLocationsByAreaInput,
    IFullOrgUnitAvailability
} from '@msdyn365-commerce-modules/retail-actions';

import {
    IFranchise,
    GetFranchiseData,
    getFranchise
} from '../../data-actions/get-franchise-data.action';

import {
    getTelemetryObject,
    IModuleProps,
    INodeProps,
    ITelemetryContent,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    NodeTag
} from '@msdyn365-commerce-modules/utilities';
import { OrgUnitLocation } from '@msdyn365-commerce/retail-proxy';
import classname from 'classnames';
import Cookies from 'universal-cookie';
import getSociData, { GetSociDataInput } from '../../actions/get-soci-data.action';
import { ProductCompareBuyboxStateSharing } from '../../actions/product-compare-buybox-state-sharing';
import { getZipCode, getGeoLocationZipCode } from '../../Utilities/get-zipcode';

export interface IStoreSelectorState {
    isSearchInProgress: boolean;
    searchTerm?: string;
    isSeeAllStoreValue: boolean;
    isLocationDisabled?: boolean;
    // VSI Customization - starts
    isError: boolean;
    isFranchiseSearchInProgress: boolean;
    franchiseList: IFranchise[];
    isSociLocationsInProgress: boolean;
    sociLocationsList: any;
    // VSI Customization - ends
}

// VSI Customization - starts
export type storeLocatorCookie = {
    name: string;
    value: string | undefined;
};
// VSI Customization - ends
export interface IStoreSelectorViewProps extends IMfrmStoreSelectorProps<IMfrmStoreSelectorData> {
    state: IStoreSelectorState;

    // Containers:
    ModuleProps: IModuleProps;
    BodyContainerProps: INodeProps;
    FooterContainerProps: INodeProps;
    HeaderContainerProps: INodeProps;
    HeaderProps?: React.ReactNode;

    // Components
    locationsMessage?: React.ReactNode;
    search?: React.ReactNode;
    spinner?: React.ReactNode;
    locationsList?: React.ReactNode;
    sociLocationsList?: React.ReactNode;
    franchiseList?: React.ReactNode;
    noLocationsMessage?: React.ReactNode;
    terms?: React.ReactNode;
    headerText?: React.ReactNode;
    // VSI Customization - starts
    banner?: JSX.Element;
    seeAllStores?: React.ReactNode;
    currentLocation?: JSX.Element;
    isError?: boolean;
    // VSI Customization - ends

    callbacks: {
        closeDialog(): Promise<void>;
        searchTermChanged(searchTerm: string): Promise<void>;
        // VSI Customization - starts
        isErrorChanged(isError: boolean): Promise<void>;
        // VSI Customization - ends
        performSearch(searchTerm: string): Promise<void>;
        selectedLocation(location: OrgUnitLocation | undefined): Promise<void>;
        getAndUpdateUserLocation(): Promise<void>;
        setPreferredStore(storeId: string | undefined): Promise<void>;
        removePreferredStore(): Promise<void>;
        setSelectedStore(storeId: string | undefined): Promise<void>;
        setOrgUnitStoreInformation(location: OrgUnitLocation | undefined): Promise<void>;
    };
}

/**
 *
 * Pick Up In Store module
 * @extends React.Component<IPickUpInStoreProps<IPickUpInStoreData>>}
 */
@observer
class StoreSelector extends React.Component<IMfrmStoreSelectorProps<IMfrmStoreSelectorData>, IStoreSelectorState> {
    private telemetryContent: ITelemetryContent;

    constructor(props: IMfrmStoreSelectorProps<IMfrmStoreSelectorData>) {
        super(props);
        // VSI Customization - starts
        const cookies = new Cookies();
        const zipCode = cookies.get('zip_code');
        this.state = {
            isSearchInProgress: true,
            searchTerm: zipCode,
            isSeeAllStoreValue: false,
            isError: false,
            isFranchiseSearchInProgress: true,
            franchiseList: [],
            isSociLocationsInProgress: true,
            sociLocationsList: []
        };
        // VSI Customization - ends
        this.telemetryContent = getTelemetryObject(
            this.props.context.request.telemetryPageName!,
            this.props.friendlyName,
            this.props.telemetry
        );
    }

    // tslint:disable-next-line: max-func-body-length
    public componentDidMount(): void {
        const {
            context: {
                actionContext
            },
            app: {
                config: { enableSociLocations }
            },
            data: {
                storeSelectorState: { result: storeSelectorState },
                storeSelectorStateManager
            }
        } = this.props;

        if ((enableSociLocations && !storeSelectorState?.sociPreferredStore) || (!enableSociLocations && !storeSelectorStateManager.result?.preferredStore)) {
            const defaultZipCode = getZipCode(actionContext) || '';
            // VSI Customization - starts
            const cookies = new Cookies();
            const zipCode = cookies.get('zip_code');
            // @ts-ignore
            const selectedStore = localStorage.getItem('_msdyn365__preferredStore_') ? JSON.parse(localStorage.getItem('_msdyn365__preferredStore_')) : null;
            if (!zipCode || zipCode !== this.state.searchTerm || !selectedStore) {
                this._setCookie({ name: 'zip_code', value: defaultZipCode }, 24 * 365);
                // Todo: Need to handle promises in a proper way
                // eslint-disable-next-line @typescript-eslint/no-floating-promises
                this._setDefaultStore(defaultZipCode);
            }
        }
        // Todo: Need to handle promises in a proper way
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this._getAndUpdateStoreLocation();

        reaction(
            () => this.props.data.storeSelectorStateManager.result && this.props.data.storeSelectorStateManager.result.isDialogOpen,
            async () => {
                const context = this.props.data.storeSelectorStateManager.result?.context;
                const selectedStore = localStorage.getItem('_msdyn365__preferredStore_') ? JSON.parse(localStorage.getItem('_msdyn365__preferredStore_')!) : null;
                // VSI Customization - starts
                const storeSelectorState = this.props.data.storeSelectorState?.result;
                this.props.context.actionContext.update(new StoreSelectorStateInput(), {
                    ...storeSelectorState,
                    isModalOpen:
                        this.props.data.storeSelectorStateManager.result && this.props.data.storeSelectorStateManager.result.isDialogOpen,
                    isSociLocationsEnabled: this.props.app.config.enableSociLocations,
                    sociPreferredStore: selectedStore

                });
                // VSI Customization - starts
                if (context && context?.id === this.props.id) {
                    // Todo: Need to handle promises in a proper way
                    // eslint-disable-next-line @typescript-eslint/no-floating-promises
                    this._getAndUpdateStoreLocation();
                }
            }
        );

        reaction(
            () =>
                this.props.data.storeSelectorStateManager.result &&
                this.props.data.storeSelectorStateManager.result.context &&
                this.props.data.storeSelectorStateManager.result.context.id,
            async () => {
                const cookies = new Cookies();
                const zipCode = cookies.get('zip_code');
                const context = this.props.data.storeSelectorStateManager.result?.context;
                if (context && context?.id === this.props.id) {
                    await this._searchTermChanged(zipCode);
                    // Todo: Need to handle promises in a proper way
                    // eslint-disable-next-line @typescript-eslint/no-floating-promises
                    this._getAndUpdateStoreLocation();
                }
            }
        );

    }

    public shouldComponentUpdate(nextProps: IMfrmStoreSelectorProps<IMfrmStoreSelectorData>): boolean {
        const {
            data: {
                storeSelectorStateManager: { result: storeSelectorStateManager }
            }
        } = this.props;
        const context = storeSelectorStateManager ? storeSelectorStateManager.context : undefined;
        if (context && context.id === this.props.id) {
            return true;
        }
        return false;
    }

    // tslint:disable-next-line:max-func-body-length cyclomatic-complexity
    public render(): JSX.Element {
        const {
            config: {
                termsOfServiceLink,
                className,
                heading,
                mode,
                style,
                setAsPreferredStore,
                showAllStores,
                lookupRadius,
                // VSI Customization - starts
                showBanner,
                enableOOBShowAllStores,
                showAllStoresExternalLink
                // VSI Customization - ends
            },
            context: {
                // 27481 telemetry,
                app: { config: appConfig },
                actionContext: {
                    requestContext: {
                        // 27481 channel,
                        cookies
                    }
                }
            },
            resources,
            data: {
                availabilitiesWithHours: { result: availabilitiesWithHours },
                storeSelectorStateManager: { result: storeSelectorStateManager },
                storeLocations: { result: storeLocations }
            }
        } = this.props;

        // 27481 const isMapDisabled = (channel && channel.BingMapsApiKey && channel.BingMapsEnabled) ? false : true;
        const context = storeSelectorStateManager ? storeSelectorStateManager.context : undefined;
        const isDialogOpen: boolean = storeSelectorStateManager
            ? storeSelectorStateManager.isDialogOpen && context?.id === this.props.id
            : false;
        const styleDefault = style !== undefined ? style : stylyEnum.dialog;
        const modeDefault = mode !== undefined ? mode : modeEnum.pickupInStore;
        const inlineView = styleDefault === stylyEnum.inline;
        const locatorView = modeDefault === modeEnum.findStores;
        const hideInventory = locatorView;
        const selectedStoreLocationId = storeSelectorStateManager?.selectedStoreLocationId;
        const preferredStore: IStoreInfo | undefined = storeSelectorStateManager?.preferredStore;
        const displayList = storeSelectorStateManager?.listMapViewState?.displayList;
        const isPreferredStoreEnabled = setAsPreferredStore && cookies?.isConsentGiven();
        const sociPrefferedStore =
            MsDyn365.isBrowser && localStorage.getItem('_mfrm__prefferedStore_')
                ? JSON.parse(localStorage.getItem('_mfrm__prefferedStore_') || '')
                : {};
        const projectMetaSoci = sociPrefferedStore?.project_meta ? JSON.parse(sociPrefferedStore?.project_meta) : '';
        const showLocationView =
            context &&
            (context.showMapViewLink === undefined || context.showMapViewLink) &&
            locatorView &&
            storeSelectorStateManager?.isMapModuleLoaded;

        const StoreSelectorActions = {
            toggleListMapViewState(): void {
                storeSelectorStateManager?.toggleListMapViewState();
            }
        };
        /*27481 if (!(channel && channel.BingMapsApiKey)) {
            telemetry.error('BingMapsApiKey is missing.');
        }*/

        const configHeading = heading ? heading : locatorView ? resources.storeLocatorHeaderText : resources.storeSelectorHeaderText;
        const preferredDistanceUnit =
            this.props.config.searchRadiusUnit === searchRadiusUnit.kilometers ? resources.kilometersShortText : resources.milesShortText;
        const viewProps = {
            ...(this.props as IMfrmStoreSelectorProps<IMfrmStoreSelectorData>),
            state: this.state,

            ModuleProps: {
                tag: inlineView ? ('div' as NodeTag) : Modal,
                moduleProps: this.props,
                className: classname(`ms-store-${inlineView ? 'locate' : 'select'}`, className),
                autoFocus: true,
                fade: true,
                isOpen: isDialogOpen,
                horizontalPosition: 'center',
                verticalPosition: 'center',
                // VSI Customization - starts
                modalClassName: 'ms-store-select-dialog',
                toggle: isDialogOpen ? this._closeDialog : storeSelectorStateManager?.openDialog
                // VSI Customization - ends
            },
            HeaderContainerProps: {
                tag: inlineView ? ('div' as NodeTag) : ModalHeader,
                className: `ms-store-select__${inlineView ? 'header' : 'modal-header'}`,
                toggle: isDialogOpen ? this._closeDialog : storeSelectorStateManager?.openDialog
            },
            HeaderProps: configHeading && (
                <Msdyn365.Text
                    className='msc-modal__title'
                    {...configHeading}
                    tag={'h2'}
                    text={configHeading}
                    editProps={{ onEdit: this.handleHeadingChange, requestContext: this.props.context.request }}
                />
            ),
            FooterContainerProps: {
                tag: inlineView ? ('div' as NodeTag) : ModalFooter,
                className: `ms-store-select__${inlineView ? 'footer' : 'modal-footer'} ${!displayList ? 'map-only-view' : ''}`
            },
            BodyContainerProps: {
                tag: inlineView ? ('div' as NodeTag) : ModalBody,
                className: `ms-store-select__${inlineView ? 'body' : 'modal-body'}`
            },
            locationsMessage: !this.state.isLocationDisabled && (
                <StoreSelectorFoundLocationsMessage
                    resources={resources}
                    locations={hideInventory ? storeLocations : availabilitiesWithHours}
                    searchRadius={lookupRadius || 50}
                    preferredDistanceUnit={preferredDistanceUnit}
                    isSeeAllStore={this.state.isSeeAllStoreValue}
                />
            ),
            search: (
                <StoreSelectorSearchForm
                    value={this.state.searchTerm || ''}
                    resources={resources}
                    performSearch={this._performSearch}
                    searchTermChanged={this._searchTermChanged}
                    isMapDisabled={undefined}
                    // VSI Customization - starts
                    isError={this.state.isError || false}
                    isErrorChanged={this._isErrorChanged}
                    // showAllStores={showAllStores}
                    id={this.props.id}
                    // tslint:disable-next-line:react-this-binding-issue jsx-no-lambda
                    // onShowAllStores={() => this._getAndUpdateStoreLocation(true)}
                    // VSI Customization - starts
                    onToggleListMapViewState={StoreSelectorActions.toggleListMapViewState}
                    displayList={displayList}
                    locatorView={showLocationView}
                    telemetryContent={this.telemetryContent}
                />
            ),
            spinner: <StoreSelectorWaiting />,
            franchiseList: (this.props.config.enableFranchise && this.state.franchiseList.length > 0) ? (
                <div className='ms-store-select__locations show franchise' role="tablist">
                    {this.state.franchiseList.sort((n1, n2) => (n1.miles || 0) - (n2.miles || 0)).map(franchise => {
                        return (
                            <div className='ms-store-select__location' role='tab' key={franchise.address_line_1}>
                                <div className='ms-store-select__location-line-item'>
                                    <div className='ms-store-select__location-line-item-header'>
                                        <span className='franchise-pill'>Franchise</span>
                                        <span className='ms-store-select__location-line-item-store-name'>{franchise.name}</span>
                                    </div>
                                    <div className="ms-store-select__location-line-item-store-hours">
                                        <span className="ms-store-select__location-line-item-store-distance"> {(franchise.miles)?.toFixed(0)} mi</span></div>
                                    <div className="ms-store-select__location-line-item-content">
                                        <div className="ms-store-select__location-line-item-contact-info">
                                            <p className="ms-store-select__location-line-header">Contact</p>
                                            <div className="ms-store-select__location-line-shop-address-section">
                                                <span className="msi msi-map-marker-alt ms-store-select__location-line-shop-address-glyph-icon"></span>
                                                <div className="ms-store-select__location-line-shop-address-text">
                                                    <p>{franchise.address_line_1} {franchise.address_line_2}</p>
                                                    <p>{franchise.city} {franchise.state_province} {franchise.postal_code}</p>
                                                </div>
                                            </div>
                                        </div>
                                        {franchise.website_url !== "" && (
                                            <div className="ms-store-select__location-line-item-btn-holder">
                                                <a className="ms-store-select__location-line-item-store-details" style={{ float: 'right' }}
                                                    href={franchise.website_url} target="_blank">Franchise Details</a>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            ) : null,
            locationsList: ((availabilitiesWithHours && availabilitiesWithHours.length) || (storeLocations && storeLocations.length)) && (
                <StoreSelectorLocationLines
                    locations={hideInventory ? storeLocations : availabilitiesWithHours}
                    resources={resources}
                    preferredDistanceUnit={preferredDistanceUnit}
                    alreadySelectedLocation={context ? context.alreadySelectedLocation : undefined}
                    outOfStockThreshold={appConfig.outOfStockThreshold || 0}
                    enableOutOfStockCheck={appConfig.enableStockCheck || false}
                    onLocationSelected={this._onOrgUnitLocationSelected}
                    storeLocatorView={locatorView}
                    selectedStoreLocationId={selectedStoreLocationId}
                    onClick={this._setSelectedStore}
                    preferredStoreLocationId={preferredStore?.StoreId ? preferredStore.StoreId : ''}
                    onSetAsPreferredStore={this._setPreferredStore}
                    onRemovePreferredStore={this._removePreferredStore}
                    isPreferredStoreEnabled={isPreferredStoreEnabled}
                    displayList={displayList}
                    enableFranchise={this.props.config.enableFranchise}
                    telemetryContent={this.telemetryContent}
                    isLocationDisabled={this.state.isLocationDisabled}
                />
            ),
            sociLocationsList: (
                <SociLocationLines
                    locations={this.state.sociLocationsList}
                    resources={resources}
                    preferredDistanceUnit={preferredDistanceUnit}
                    onLocationSelected={this._onOrgUnitLocationSelected}
                    storeLocatorView={locatorView}
                    selectedStoreLocationId={selectedStoreLocationId}
                    onClick={this._setSelectedStore}
                    preferredStoreLocationId={projectMetaSoci ? projectMetaSoci['Corporate ID'] : ''}
                    onSetAsPreferredStore={this._onSetPreferredSociStore}
                    onRemovePreferredStore={this._onRemovePreferredSociStore}
                    isPreferredStoreEnabled={isPreferredStoreEnabled}
                    displayList={displayList}
                    telemetryContent={this.telemetryContent}
                    isLocationDisabled={this.state.isLocationDisabled}
                />
            ),
            // VSI Customization - starts
            noLocationsMessage: <StoreSelectorEmptyMessage resources={resources} config={this.props.config} />,
            // VSI Customization - ends
            terms: (
                <StoreSelectorTermsOfService
                    link={termsOfServiceLink}
                    onTextChange={this.handleLinkTextChange}
                    requestContext={this.props.context.request}
                />
            ),
            // Not being used in current context, we can use it here to pass more props.
            headerText: (
                <span>{heading ? heading : locatorView ? resources.storeLocatorHeaderText : resources.storeSelectorHeaderText}</span>
            ),

            // VSI Customization - starts
            seeAllStores: (
                <StoreSelectorSeeAllStores
                    resources={resources}
                    telemetryContent={this.telemetryContent}
                    showAllStores={showAllStores}
                    enableOOBShowAllStores={enableOOBShowAllStores}
                    showAllStoresExternalLink={showAllStoresExternalLink}
                    // tslint:disable-next-line:react-this-binding-issue jsx-no-lambda
                    onShowAllStores={() => this._getAndUpdateStoreLocation(true)}
                />
            ),
            isError: this.state.isError,
            banner: (showBanner && this._renderBannerText) || null,
            currentLocation: this._renderCurrentLocation,
            // VSI Customization - ends
            callbacks: {
                searchTermChanged: this._searchTermChanged,
                performSearch: this._performSearch,
                closeDialog: this._closeDialog,
                selectedLocation: this._onOrgUnitLocationSelected,
                // Todo: Need to handle promises in a proper way
                // eslint-disable-next-line @typescript-eslint/no-floating-promises
                getAndUpdateUserLocation: () => {
                    // eslint-disable-next-line @typescript-eslint/no-floating-promises
                    this._getAndUpdateStoreLocation();
                    return Promise.resolve();
                },
                onSuggestionSelected: this._onOrgUnitLocationSelected,
                setPreferredStore: this._setPreferredStore,
                removePreferredStore: this._removePreferredStore,
                setSelectedStore: this._setSelectedStore,
                setOrgUnitStoreInformation: this._setOrgUnitStoreInformation,
                // VSI Customization - starts
                isErrorChanged: this._isErrorChanged
                // VSI Customization - ends
            }
        } as IStoreSelectorViewProps;

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    public handleLinkTextChange = (event: Msdyn365.ContentEditableEvent) => {
        if (this.props.config.termsOfServiceLink) {
            this.props.config.termsOfServiceLink.linkText = event.target.value;
        }
    };
    public handleHeadingChange = (event: Msdyn365.ContentEditableEvent) => (this.props.config.heading = event.target.value);
    private _setPreferredStore = async (storeId: string | undefined): Promise<void> => {
        if (!storeId) {
            return;
        }

        const {
            context: {
                actionContext,
                actionContext: {
                    requestContext: { cookies }
                }
            },
            config: { mode },
            data: {
                storeSelectorStateManager: { result: storeSelectorStateManager },
                storeLocations: { result: storeLocations },
                availabilitiesWithHours: { result: availabilitiesWithHours }
            }
        } = this.props;

        if (!storeSelectorStateManager) {
            return;
        }
        const modeDefault = mode !== undefined ? mode : modeEnum.pickupInStore;

        let stores: IFullOrgUnitAvailability[];
        if (modeDefault === modeEnum.findStores && storeLocations) {
            stores = storeLocations;
        } else {
            stores = availabilitiesWithHours!;
        }

        const preferedStore = stores.find(store => store.OrgUnitAvailability?.OrgUnitLocation?.OrgUnitNumber === storeId);
        // @ts-ignore
        const selectedStore = localStorage.getItem('_msdyn365__preferredStore_') ? JSON.parse(localStorage.getItem('_msdyn365__preferredStore_')) : null;
        // VSI Customization - start
        const storeSelectorState = this.props.data.storeSelectorState?.result;
        this.props.context.actionContext.update(new StoreSelectorStateInput(), {
            ...storeSelectorState,
            prefferedStore: preferedStore?.OrgUnitAvailability?.OrgUnitLocation
        });
        localStorage.setItem('_mfrm__prefferedStore_', JSON.stringify(preferedStore?.OrgUnitAvailability?.OrgUnitLocation));
        this._setCookie({ name: 'preferredStoreState', value: preferedStore?.OrgUnitAvailability?.OrgUnitLocation?.State || '' }, 24 * 365);
        const zip_Code = this.state.searchTerm ? this.state.searchTerm : getZipCode(actionContext);
        // On setting preferred store, current search zipCode will be used as delivery zipCode
        this._setCookie({ name: 'zip_code', value: zip_Code || '' }, 24 * 365);
        // VSI Customization - ends
        // update the state to trigger zipcode change in product-compare
        this.props.context.actionContext.update(new ProductCompareBuyboxStateSharing(zip_Code), { zipcode: zip_Code });
        const pageURL = MsDyn365.isBrowser && window.location.pathname;
        if (selectedStore && preferedStore?.OrgUnitAvailability?.OrgUnitLocation?.OrgUnitNumber !== selectedStore?.StoreId) {
            // @ts-ignore
            if ('utag' in window) {
                // @ts-ignore
                utag.link({
                    event: 'store-locator-slideout-change-my-store',
                    event_category: 'store locator slideout',
                    event_action: 'change my store',
                    event_label: `from: ${pageURL}`,
                    event_noninteraction: 'false'
                });
            }
        }
        await storeSelectorStateManager.setPreferredStoreLocation(
            storeId,
            preferedStore?.OrgUnitAvailability?.OrgUnitLocation?.OrgUnitName,
            cookies?.isConsentGiven(),
            preferedStore?.OrgUnitAvailability?.OrgUnitLocation?.Latitude,
            preferedStore?.OrgUnitAvailability?.OrgUnitLocation?.Longitude
        );

        Msdyn365.msdyn365Commerce.isBrowser && document.body.classList.remove('modal-store-locator-toggle');
        Msdyn365.msdyn365Commerce.isBrowser && document.body.classList.remove('modal-store-locator-toggle-pdp');
        await storeSelectorStateManager.closeDialog();
    };

    private _removePreferredStore = async (): Promise<void> => {
        const {
            context: {
                actionContext: {
                    requestContext: { cookies }
                }
            },
            data: {
                storeSelectorStateManager: { result: storeSelectorStateManager }
            }
        } = this.props;

        if (!storeSelectorStateManager) {
            return;
        }

        await storeSelectorStateManager.clearPreferredStore(cookies?.isConsentGiven());
        // VSI Customization - start
        localStorage.removeItem('_mfrm__prefferedStore_');
        // VSI Customization - ends
    };

    private _setSelectedStore = async (storeId: string | undefined): Promise<void> => {
        const {
            data: {
                storeSelectorStateManager: { result: storeSelectorStateManager }
            }
        } = this.props;

        if (!storeSelectorStateManager) {
            return;
        }

        await storeSelectorStateManager.setSelectedStoreLocationId(storeId);
    };

    private _sortSociLocations = (stores: any) => {
        const preferredStore = localStorage?.getItem('_mfrm__prefferedStore_') || null;
        if (stores?.length !== 0 && preferredStore) {
            const parsedPreferredStore = JSON.parse(preferredStore);
            const selectedStoreIndex = stores?.findIndex((store: any) => parsedPreferredStore?.name === store.name);
            const selectedStore = stores?.splice(selectedStoreIndex, 1);
            stores.unshift(selectedStore[0]);
            return [...new Set(stores)];
        }
        return stores;
    };

    private _performSearch = async (searchTerm: string): Promise<void> => {
        const {
            context: { actionContext },
            config: {
                lookupRadius,
                maxNoOfFranchise
            },
            data: {
                storeSelectorStateManager: { result: storeSelectorStateManager }
            }
        } = this.props;

        if (!searchTerm || !storeSelectorStateManager) {
            return;
        }

        this.setState({ isSearchInProgress: true, isSeeAllStoreValue: false, isFranchiseSearchInProgress: true, isSociLocationsInProgress: true });

        if (!this.props.app.config.enableSociLocations) {
            const location = await storeSelectorStateManager.geoLocate(searchTerm.trim() !== '' ? searchTerm : '0', actionContext);

            if (location) {
                await this._updateLocations(location.latitude, location.longitude);
            } else {
                await this._updateLocations();
            }
        } else {
            try {
                this.setState({ isSociLocationsInProgress: true });
                void getSociData(new GetSociDataInput(lookupRadius, maxNoOfFranchise, searchTerm), this.props.context.actionContext).then((data: any) => {
                    if (data) {
                        const sociAvailableStores = data.response.collection && data.response.collection.filter((store: { opening_status: string; }) => store?.opening_status !== 'permanently_closed') || [];
                        const sortedLocations = this._sortSociLocations(sociAvailableStores);
                        this.setState({ sociLocationsList: sortedLocations, isSociLocationsInProgress: false });
                        this._setCookie({ name: 'zip_code', value: searchTerm }, 24 * 365);
                    }
                });
            } catch (error) {
                console.error('Soci API error', Error);
            }
        }


        this.setState({ isSearchInProgress: false, isFranchiseSearchInProgress: false, isSociLocationsInProgress: false });
    };

    private _searchTermChanged = async (searchTerm: string): Promise<void> => {
        this.setState({ searchTerm: searchTerm });
    };

    // VSI Customization - starts
    private _isErrorChanged = async (isError: boolean): Promise<void> => {
        this.setState({ isError: isError });
    };
    // VSI Customization - ends

    private _getAndUpdateStoreLocation = async (showAllStores?: boolean): Promise<void> => {
        const {
            config: { style, maxNoOfFranchise, lookupRadius },
            context: { actionContext },
            data: {
                storeSelectorStateManager: { result: storeSelectorStateManager }
            }
        } = this.props;

        const styleDefault = style !== undefined ? style : stylyEnum.dialog;

        if (styleDefault !== stylyEnum.inline && (!storeSelectorStateManager || !storeSelectorStateManager.isDialogOpen)) {
            // Store selector dialog isn't open yet, so don't try and update anything
            return;
        }
        // VSI Customization - starts
        const zipCode = getZipCode(actionContext) || '';

        // SOCi Implementation
        // if soci config enable then get and set data from soci
        // call soci data action here
        // else call previous method as it is
        if (!this.props.app.config.enableSociLocations) {
            this.setState({ isSearchInProgress: true, isSeeAllStoreValue: false, isFranchiseSearchInProgress: false });
            const location =
                storeSelectorStateManager && (await storeSelectorStateManager.geoLocate(zipCode?.trim() !== '' ? zipCode : '0', actionContext));
            if (location) {
                if (showAllStores) {
                    this.setState({ isSeeAllStoreValue: true });
                    await this._searchTermChanged(zipCode);

                    await this._isErrorChanged(false);
                    await this._updateLocations(location.latitude, location.longitude, 0);
                } else {
                    await this._searchTermChanged(zipCode);
                    await this._isErrorChanged(false);

                    await this._updateLocations(location.latitude, location.longitude);
                }

                this.setState({ isSearchInProgress: false, isFranchiseSearchInProgress: false });
            } else {
                this.setState({ isSearchInProgress: false, isFranchiseSearchInProgress: false });
            }
        } else {
            this.setState({ isSearchInProgress: false, isSeeAllStoreValue: false, isFranchiseSearchInProgress: false, isSociLocationsInProgress: true });
            await this._searchTermChanged(zipCode);
            try {
                this.setState({ isSociLocationsInProgress: true });
                void getSociData(new GetSociDataInput(lookupRadius, maxNoOfFranchise, zipCode), this.props.context.actionContext).then((data: any) => {
                    if (data) {
                        const sociAvailableStores = data.response.collection && data.response.collection.filter((store: { opening_status: string; }) => store?.opening_status !== 'permanently_closed') || [];
                        const sortedLocations = this._sortSociLocations(sociAvailableStores);
                        this.setState({ sociLocationsList: sortedLocations, isSociLocationsInProgress: false });
                    }
                });
            } catch (error) {
                console.error('Soci API error', Error);
            }
        }
    };

    private _closeDialog = async (): Promise<void> => {
        // VSI Customization - starts
        Msdyn365.msdyn365Commerce.isBrowser && document.body.classList.remove('modal-store-locator-toggle');
        Msdyn365.msdyn365Commerce.isBrowser && document.body.classList.remove('modal-store-locator-toggle-pdp');
        // VSI Customization - ends
        const {
            data: {
                storeSelectorStateManager: { result: storeSelectorStateManager }
            }
        } = this.props;

        if (storeSelectorStateManager) {
            return storeSelectorStateManager.closeDialog();
        }
    };

    private _setOrgUnitStoreInformation = async (orgUnitStoreInformation: IFullOrgUnitAvailability[]): Promise<void> => {
        const {
            data: {
                storeSelectorStateManager: { result: storeSelectorStateManager }
            },
            config: { style },
            telemetry
        } = this.props;

        if (!storeSelectorStateManager) {
            return;
        }

        const context = storeSelectorStateManager.context;
        const id: string = context && context.id ? context.id : this.props.id;
        const styleDefault = style !== undefined ? style : stylyEnum.dialog;
        await storeSelectorStateManager
            .setOrgUnitStoreInformation({
                id,
                showMapViewLink: context && context.showMapViewLink,
                isDialog: styleDefault === stylyEnum.dialog,
                orgUnitStoreInformation,
                onLocationSelected: orgUnitLocation => {
                    // TO-DO handle event for when location in selected
                    return Promise.resolve();
                }
            })
            .catch((error: Error) => {
                if (telemetry) {
                    telemetry.error(error.message);
                    telemetry.debug('Unable to Update store location state');
                }
                return;
            });
        return;
    };

    private _updateLocations = async (latitude?: number, longitude?: number, radius?: number): Promise<void> => {
        const {
            telemetry,
            config: { mode, lookupRadius, maxNoOfFranchise },
            context: { actionContext },
            data: {
                storeSelectorStateManager: { result: storeSelectorStateManager }
            }
        } = this.props;

        const calRadius = radius || radius === 0 ? radius : lookupRadius || 50;
        const distanceUnitValue = this.props.config.searchRadiusUnit === searchRadiusUnit.kilometers ? 1 : 0;
        const modeDefault = mode !== undefined ? mode : modeEnum.pickupInStore;
        const preferredStore: IStoreInfo | undefined = storeSelectorStateManager?.preferredStore;
        const lat = this.state.searchTerm || !(preferredStore && preferredStore.Latitude) ? latitude : preferredStore.Latitude;
        const long = this.state.searchTerm || !(preferredStore && preferredStore.Longitude) ? longitude : preferredStore.Longitude;


        // Get Franchise Data
        const getFranchiseInputInput = new GetFranchiseData(
            lat,
            long,
            calRadius
        );

        const franchiseStore = await getFranchise(getFranchiseInputInput, actionContext);
        const maxFranchises = maxNoOfFranchise ? franchiseStore.splice(0, maxNoOfFranchise) : franchiseStore;
        if (franchiseStore) {
            this.setState({ isFranchiseSearchInProgress: false, franchiseList: maxFranchises });
        }

        if (modeDefault === modeEnum.findStores) {
            const orgUnitLocationsInput = new GetOrgUnitLocationsByAreaInput(
                lat,
                long,
                calRadius,
                distanceUnitValue,
                this.state.isLocationDisabled
            );

            const orgUnitLocations = await getOrgUnitLocationsByArea(orgUnitLocationsInput, actionContext);
            if (orgUnitLocations) {
                await this._setOrgUnitStoreInformation(orgUnitLocations);
                await this._setSelectedStore(undefined);
            }
            return;
        }

        if (!storeSelectorStateManager || !storeSelectorStateManager.context) {
            // Store selector dialog isn't open yet, so don't try and update anything
            return;
        }

        if (!storeSelectorStateManager.context.product) {
            // TODO: Use alternative search method here to not make inventory check
            return;
        }

        const input = new GetFullAvailableInventoryNearbyInput(
            storeSelectorStateManager.context.product.RecordId,
            lat || 0,
            long || 0,
            calRadius,
            distanceUnitValue,
            this.state.isLocationDisabled
        );

        await getFullAvailableInventoryNearby(input, actionContext).catch(error => {
            telemetry.warning(error);
            telemetry.error(error.message);
            telemetry.debug(`Unable to get full available inventory near by`);
        });
    };

    private _onOrgUnitLocationSelected = async (location: OrgUnitLocation | undefined): Promise<void> => {
        const {
            telemetry,
            data: {
                storeSelectorStateManager: { result: storeSelectorStateManager }
            }
        } = this.props;

        if (storeSelectorStateManager) {
            if (location && storeSelectorStateManager.context) {
                // Want to eat exceptions here so that the dialog still closes even if the onLocationSelected call goes wrong
                await storeSelectorStateManager.context.onLocationSelected(location).catch(error => {
                    telemetry.warning(error);
                    telemetry.error(error.message);
                    telemetry.debug(`Unable to get on org unit location selected`);
                });
            }

            await storeSelectorStateManager.closeDialog();
        }
    };

    // VSI Customization - starts
    private _setCookie(cookie: storeLocatorCookie, deltaHoursFromToday: number): void {
        const { cookies } = this.props.context.request;
        const now = new Date(Date.now());
        now.setHours(now.getHours() + deltaHoursFromToday);

        cookies.set(cookie.name, cookie.value, {
            expires: deltaHoursFromToday === 0 || deltaHoursFromToday === undefined ? 0 : now,
            isEssential: true
        } as {});
    }

    private get _renderBannerText(): JSX.Element {
        const { bannerText } = this.props.config;
        return <Msdyn365.RichTextComponent className='ms-store-select__covid-message' text={bannerText} />;
    }

    private get _renderCurrentLocation(): JSX.Element {
        return (
            // tslint:disable-next-line:react-this-binding-issue jsx-no-lambda
            <div
                className='msc-modal__current-location'
                role='link'
                onClick={() => this._getCurrentLocationStores()}
            >
                Use current location
            </div>
        );
    }

    private _setDefaultStore = async (searchTerm: string): Promise<void> => {
        const {
            config: { lookupRadius, maxNoOfFranchise },
            context: { actionContext },
            data: { storeSelectorStateManager }
        } = this.props;

        if (!searchTerm || !storeSelectorStateManager) {
            return;
        }

        if (this.props.app.config.enableSociLocations) {
            try {
                await this._searchTermChanged(searchTerm);
                void getSociData(new GetSociDataInput(lookupRadius, maxNoOfFranchise, searchTerm), this.props.context.actionContext).then((data: any) => {
                    if (data && data.response.collection) {
                        const sociAvailableStores = data.response.collection && data.response.collection.filter((store: { opening_status: string; }) => store?.opening_status !== 'permanently_closed') || [];
                        const sortedLocations = this._sortSociLocations(sociAvailableStores);
                        this.setState({ sociLocationsList: sortedLocations });
                        this._setCookie({ name: 'zip_code', value: searchTerm }, 24 * 365);
                        void this._onSetPreferredSociStore(data.response.collection[0]);
                    }
                });
            } catch (error) {
                console.error('Soci API error', Error);
            }
        } else {
            try {
                const location = await (await storeSelectorStateManager).geoLocate(searchTerm.trim() !== '' ? searchTerm : '0', actionContext);
                const lat = location?.latitude || 0;
                const long = location?.longitude || 0;
                const calRadius = lookupRadius || 50;
                const distanceUnitValue = this.props.config.searchRadiusUnit === searchRadiusUnit.kilometers ? 1 : 0;
                const orgUnitLocationsInput = new GetOrgUnitLocationsByAreaInput(
                    lat,
                    long,
                    calRadius,
                    distanceUnitValue,
                    this.state.isLocationDisabled
                );

                const orgUnitLocations = await getOrgUnitLocationsByArea(orgUnitLocationsInput, actionContext);
                if (orgUnitLocations) {
                    const defaultStoreId =
                        orgUnitLocations &&
                        orgUnitLocations[0]?.OrgUnitAvailability &&
                        orgUnitLocations[0].OrgUnitAvailability?.OrgUnitLocation?.OrgUnitNumber;
                    // Todo: Need to handle promises in a proper way
                    // tslint:disable-next-line:no-floating-promises
                    await this._setPreferredStore(defaultStoreId);
                }
            } catch (error) {
                console.error('error', error);
            }
        }
    };

    // Method to get store locations based on current zipCode, as per max-mind's geoLocation
    private _getCurrentLocationStores = async (): Promise<void> => {
        const {
            context: { actionContext },
            config: {
                maxNoOfFranchise,
                lookupRadius
            },
            data: {
                storeSelectorStateManager: { result: storeSelectorStateManager }
            }
        } = this.props;
        const zipCode = getGeoLocationZipCode(actionContext);
        if (!this.props.app.config.enableSociLocations) {
            if (!zipCode || !storeSelectorStateManager) {
                return;
            }

            try {
                await this._searchTermChanged(zipCode);
                const location = await storeSelectorStateManager.geoLocate(
                    zipCode.trim() !== '' ? zipCode : '0',
                    actionContext
                );
                await this._updateLocations(location?.latitude || 0, location?.longitude || 0);
            } catch (error) {
                console.error('error _getCurrentLocationStores', error);
            }

        } else {
            try {
                await this._searchTermChanged(zipCode!);
                void getSociData(new GetSociDataInput(lookupRadius, maxNoOfFranchise, zipCode), this.props.context.actionContext).then((data: any) => {
                    if (data) {
                        const sociAvailableStores = data.response.collection && data.response.collection.filter((store: { opening_status: string; }) => store?.opening_status !== 'permanently_closed') || [];
                        const sortedLocations = this._sortSociLocations(sociAvailableStores);
                        this.setState({ sociLocationsList: sortedLocations });
                        this._setCookie({ name: 'zip_code', value: zipCode }, 24 * 365);
                    }
                });
            } catch (error) {
                console.error('Soci API error', Error);
            }
        }
    };
    // VSI Customization - ends

    private _onSetPreferredSociStore = async (store: any | undefined): Promise<void> => {
        const {
            context: {
                actionContext,
                actionContext: {
                    requestContext: { cookies }
                }
            },
            data: {
                storeSelectorStateManager: { result: storeSelectorStateManager },
                storeSelectorState: { result: storeSelectorState }
            }
        } = this.props;

        this._setCookie({ name: 'preferredStoreState', value: store?.state || '' }, 24 * 365);
        const zip_Code = this.state.searchTerm ? this.state.searchTerm : getZipCode(actionContext);
        // On setting preferred store, current search zipCode will be used as delivery zipCode
        this._setCookie({ name: 'zip_code', value: zip_Code || '' }, 24 * 365);
        // update the state to trigger zipcode change in product-compare
        this.props.context.actionContext.update(new ProductCompareBuyboxStateSharing(zip_Code), { zipcode: zip_Code });
        const projectMetaSoci = store?.project_meta ? JSON.parse(store?.project_meta) : '';
        const sociStore = projectMetaSoci ? projectMetaSoci['Corporate ID'] : '';
        const sociStoreID = sociStore && (sociStore?.length > 6 ? sociStore : `000000${sociStore}`?.slice(-6)) || '';
        // @ts-ignore
        const selectedStore = localStorage.getItem('_mfrm__prefferedStore_') ? JSON.parse(localStorage.getItem('_mfrm__prefferedStore_')) : null;
        localStorage.setItem('_mfrm__prefferedStore_', JSON.stringify({ ...store, OrgUnitNumber: sociStoreID, Zip: store.postalcode }));
        this.props.context.actionContext.update(new StoreSelectorStateInput(), {
            ...storeSelectorState,
            isSociLocationsEnabled: this.props.app.config.enableSociLocations,
            sociPreferredStore: { ...store, OrgUnitNumber: sociStoreID, Zip: store.postalcode }
        });
        const pageURL = MsDyn365.isBrowser && window.location.pathname;
        if (selectedStore && sociStoreID !== selectedStore?.StoreId) {
            // @ts-ignore
            if ('utag' in window) {
                // @ts-ignore
                utag.link({
                    event: 'store-locator-slideout-change-my-store',
                    event_category: 'store locator slideout',
                    event_action: 'change my store',
                    event_label: `from: ${pageURL}`,
                    event_noninteraction: 'false'
                });
            }
        }
        if (sociStoreID && selectedStore === null) {
            // @ts-ignore
            if ('utag' in window) {
                // @ts-ignore
                utag.link({
                    event: 'store-locator-slideout-make-my-store',
                    event_category: 'store locator slideout',
                    event_action: 'make my store',
                    event_label: `from: ${pageURL}`,
                    event_noninteraction: 'false'
                });
            }
        }
        await storeSelectorStateManager?.setPreferredStoreLocation(
            sociStoreID,
            store?.name,
            cookies?.isConsentGiven(),
            store?.latitude,
            store?.longitude
        );

        Msdyn365.msdyn365Commerce.isBrowser && document.body.classList.remove('modal-store-locator-toggle');
        Msdyn365.msdyn365Commerce.isBrowser && document.body.classList.remove('modal-store-locator-toggle-pdp');
        await storeSelectorStateManager?.closeDialog();
    };

    private _onRemovePreferredSociStore = async (): Promise<void> => {
        const {
            context: {
                actionContext: {
                    requestContext: { cookies }
                }
            },
            data: {
                storeSelectorStateManager: { result: storeSelectorStateManager },
                storeSelectorState: { result: storeSelectorState }
            }
        } = this.props;
        this.props.context.actionContext.update(new StoreSelectorStateInput(), {
            ...storeSelectorState,
            sociPreferredStore: {}
        });
        if (!storeSelectorStateManager) {
            return;
        }

        await storeSelectorStateManager.clearPreferredStore(cookies?.isConsentGiven());
        localStorage.removeItem('_mfrm__prefferedStore_');
        void this._getAndUpdateStoreLocation();
    };
}

export default StoreSelector;